'use client'

import { uploadImageFromFile } from '@/actions/files/upload'
import ImageWithFallback from '@/components/common/image-with-fallback'
import { communityCreationSchema } from '@/components/forms/schemas/community'
import { Form } from '@/components/ui/form'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { createCommunity } from '../actions/create-community'
import HeaderForm from './community/header'
import JoinLinkForm from './community/join-link'
import LogoForm from './community/logo'
import NameLanguageForm from './community/name-language'

// Form controller to manage multi-step form to create a community
// FIGMA: https://www.figma.com/design/ynKceAEQKn9U5PXlvlYM84/Grid-Finder?node-id=940-17607&t=PFMWXOX5jRmA3wnu-4

// This form uses hidden fields to manage the state of the form
// There is a form navigation component (FormNavigation) to manage the form steps
// All form steps are effectively blocks that contain shadcn FormField components
// The next button uses the form.trigger() function to validate the form and move to the next step

export default function FormController({ user }) {
  const [formStep, setFormStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const isMountedRef = useRef(true)
  const isSubmittingRef = useRef(false)
  const communityIdRef = useRef(null)

  const form = useForm({
    resolver: zodResolver(communityCreationSchema),
    defaultValues: {
      name: '',
      languages: 'English',
      logoImg: null,
      headerImg: null,
    },
  })

  useEffect(() => {
    let element
    if (formStep === 0) {
      element = document.getElementById('community-form-heading')
    } else if (formStep === 1 || formStep === 2) {
      element = document.getElementById('community-branding-heading')
    } else if (formStep === 3) {
      element = document.getElementById('community-link-heading')
    }
    element?.scrollIntoView({ behavior: 'smooth' })
  }, [formStep])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  async function nextButtonClick(fieldNames) {
    const isValid = await form.trigger(fieldNames)
    if (!isValid) return
    setFormStep(formStep + 1)
  }

  async function onSubmit(data) {
    if (formStep !== 3 || isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    let wasCreated = false

    try {
      communityIdRef.current ??= crypto.randomUUID()

      const payload = {
        ...data,
        communityId: communityIdRef.current,
        discordServerId:
          data.discordServerId === 'n/a' ? null : data.discordServerId,
        notificationChannelId:
          data.notificationChannelId === 'n/a'
            ? null
            : data.notificationChannelId,
      }

      if (payload.logoImg) {
        const randomId = Math.random().toString(36).substring(2, 15)
        const s3UploadKey = `community_logo_${randomId}_logoImg`
        const uploadData = await uploadImageFromFile({
          img: payload.logoImg,
          key: s3UploadKey,
        })
        payload.logoImgUrl = uploadData.Location
        payload.logoImgS3Meta = uploadData
        payload.logoImg = null
      }

      if (payload.headerImg) {
        const randomId = Math.random().toString(36).substring(2, 15)
        const s3UploadKey = `community_header_${randomId}_headerImg`
        const uploadData = await uploadImageFromFile({
          img: payload.headerImg,
          key: s3UploadKey,
        })
        payload.headerImgUrl = uploadData.Location
        payload.headerImgS3Meta = uploadData
        payload.headerImg = null
      }

      delete payload.logoImg
      delete payload.headerImg

      const res = await createCommunity(payload)
      if (res?.id) {
        // mixpanel.track('Community Created', payload)
        wasCreated = true
        router.replace(`/manage/${res.id}/racing`)
        return
      } else {
        toast.error('Failed to create community')
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      toast.error(`Failed to create community: ${message}`)
    } finally {
      if (!wasCreated) {
        isSubmittingRef.current = false

        if (isMountedRef.current) {
          setIsSubmitting(false)
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="col-span-full">
        <FormNavigation
          user={user}
          formStep={formStep}
          setFormStep={setFormStep}
          isSubmitting={isSubmitting}
        ></FormNavigation>
        <div className="col-span-full bg-neutral-blue-900 tablet:mx-auto tablet:mt-40 tablet:w-[70%] tablet:max-w-[500px]">
          {formStep === 0 && (
            <NameLanguageForm
              user={user}
              form={form}
              formStep={formStep}
              nextButtonClick={() => nextButtonClick(['name', 'languages'])}
            />
          )}

          {formStep === 1 && (
            <LogoForm
              form={form}
              formStep={formStep}
              nextButtonClick={() => nextButtonClick(['logoImg'])}
            />
          )}

          {formStep === 2 && (
            <HeaderForm
              form={form}
              formStep={formStep}
              nextButtonClick={() => nextButtonClick(['headerImg'])}
            />
          )}

          {formStep === 3 && (
            <JoinLinkForm
              form={form}
              formStep={formStep}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </form>
    </Form>
  )
}

function FormNavigation({ user, formStep, setFormStep, isSubmitting }) {
  return (
    <div className="overflow:hidden max-tablet:no-grid-padding max-w-screen col-span-full mb-9 border-b border-neutral-blue-700 bg-neutral-blue-800">
      <div className="max-tablet:screen-grid">
        <div className="col-span-full max-tablet:my-4">
          <div className="relative col-span-full h-1 w-full bg-neutral-blue-600 tablet:h-2.5">
            <div
              className={cn(
                'bg-gf-gradient min-h-1 transition-all duration-500 ease-in-out tablet:min-h-2.5',
                {
                  'w-1/4': formStep === 0,
                  'w-1/2': formStep === 1,
                  'w-2/3': formStep === 2,
                  'w-3/4': formStep === 3,
                },
              )}
            ></div>
          </div>
        </div>
      </div>

      <div className="tablet:my-5 tablet:grid tablet:w-full tablet:grid-cols-3 tablet:justify-between">
        <div className="no-scrollbar flex items-center gap-8 overflow-x-auto max-tablet:mb-8 max-tablet:w-full [&>button]:whitespace-nowrap [&>button]:text-p-2 [&>button]:font-semibold">
          <button
            type="button"
            onClick={() => setFormStep(0)}
            disabled={isSubmitting}
            className={cn(
              'cursor-pointer pl-6 text-neutral-blue-300 transition-all hover:text-shades-0 disabled:cursor-not-allowed disabled:opacity-50',
              { 'text-shades-0': formStep === 0, 'line-through': formStep > 0 },
            )}
            id="community-form-heading"
          >
            1 Community
          </button>
          <button
            type="button"
            onClick={() => setFormStep(1)}
            disabled={isSubmitting}
            className={cn(
              'cursor-pointer text-neutral-blue-300 transition-all hover:text-shades-0 disabled:cursor-not-allowed disabled:opacity-50',
              {
                'text-shades-0': formStep === 1 || formStep === 2,
                'line-through': formStep > 2,
              },
            )}
            id="community-branding-heading"
          >
            2 Branding
          </button>
          <button
            type="button"
            onClick={() => setFormStep(3)}
            disabled={isSubmitting}
            className={cn(
              'cursor-pointer pr-6 text-neutral-blue-300 transition-all hover:text-shades-0 disabled:cursor-not-allowed disabled:opacity-50',
              { 'text-shades-0': formStep === 3, 'line-through': formStep > 3 },
            )}
            id="community-link-heading"
          >
            3 Community Link
          </button>
        </div>
        <Link
          href="/"
          className="relative justify-self-center max-tablet:hidden"
        >
          <Image
            className="h-full max-h-8 w-auto cursor-pointer"
            src="/img/logos/1248x291.webp"
            alt="Grid Finder - The Ultimate Sim Racing Platform"
            width="1248"
            height="291"
            priority={true}
          />
        </Link>
        <div className="relative mr-5 aspect-square w-10 min-w-10 overflow-hidden rounded-full max-tablet:hidden tablet:justify-self-end">
          <ImageWithFallback src={getUserProfilePictureUrl(user)} fill />
        </div>
      </div>
    </div>
  )
}
